import React, { useState, useEffect, useRef } from 'react';
import ChatHeader from './components/ChatHeader';
import ChatBody from './components/ChatBody';
import MessageInput from './components/MessageInput';
import LanguageSelection from './components/LanguageSelection';
import './App.css';
import './newApp.css';
import { useTranslation } from 'react-i18next';
import { ulid } from 'ulid';
import axios from 'axios';
import { isMobile } from 'react-device-detect';
import DOMPurify from 'dompurify';
import { login } from './qpassAuthService.js';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowsAlt } from '@fortawesome/free-solid-svg-icons';
import LandingPage from './components/LandingPage.jsx';
import Loading from './components/Loading.jsx';
import { PublicClientApplication } from '@azure/msal-browser';
import { msalConfig, loginRequest } from './authConfig.js';
import FAQs from './components/Custom/FAQs';


const authMethod = process.env.REACT_APP_AUTH_METHOD;
const msalInstance = authMethod === "email" ? new PublicClientApplication(msalConfig) : null;



const App = () => {
  const [messages, setMessages] = useState([]);
  const [language, setLanguage] = useState(localStorage.getItem('language') || null);
  const [loading, setLoading] = useState(false);
  const [isChatOpen, setIsChatOpen] = useState(localStorage.getItem('isChatOpen') === 'true');
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState('ltr');
  const { t } = useTranslation();
  const [sessionId, setSessionId] = useState(ulid());
  const [placeholder, setPlaceholder] = useState(t('Type your message or use voice recording...'));
  const hasFirstMessageBeenAdded = useRef(false);
  const [account, setAccount] = useState(null);
  const [isTyping, setIsTyping] = useState(false);
  const [isSessionTimedOut, setIsSessionTimedOut] = useState(false);
  const messageInputRef = useRef(null);
  const chatIconRef = useRef(null);
  const [showChatbot, setShowChatbot] = useState(false);
  const [errorMessage, setErrorMessage] = useState(false);
  const [loginProgress, setLoginProgress] = useState("Start")
  const [isInputDisabled, setIsInputDisabled] = useState((!account || !showChatbot || isSessionTimedOut) && !errorMessage);
  const [isLoading, setIsLoading] = useState((!showChatbot || isSessionTimedOut) && !errorMessage);
  const [firstMessage, setFirstMessage] = useState("Hi, I'm TAMM, your digital assistant. I can help you with Human Resources services.");
  const [loginResponse, setLoginResponse] = useState("");
  const nodeRef = useRef(null);
  const [showChatUI, setShowChatUI] = useState(false);
  const [showAuthFallback, setShowAuthFallback] = useState(false);
  const [isAuthenticating, setIsAuthenticating] = useState(false)
  const [selectedOption, setSelectedOption] = useState(null);
  const [isTTSMuted, setIsTTSMuted] = useState(localStorage.getItem('isTTSMuted') === 'true');
  const [isTTSGloballyEnabled, setIsTTSGloballyEnabled] = useState(true);
  const currentAudioRef = useRef(null);
  const ttsAbortControllerRef = useRef(null);
  const hasUserInteractedRef = useRef(false);
  const pendingTTSTextRef = useRef(null);
  const audioContextRef = useRef(null);
  const currentSourceRef = useRef(null);

  // Lazily create a single AudioContext instance (for mobile autoplay compatibility)
  const getAudioContext = () => {
    if (!audioContextRef.current) {
      const AudioContextClass = window.AudioContext || window.webkitAudioContext;
      if (AudioContextClass) {
        audioContextRef.current = new AudioContextClass();
      }
    }
    return audioContextRef.current;
  };

  // Resume suspended AudioContext — must be called during a user gesture
  const ensureAudioUnlocked = () => {
    const ctx = getAudioContext();
    if (ctx && ctx.state === 'suspended') {
      ctx.resume().catch(() => {});
    }
  };

  // Fetch global TTS setting from CosmosDB
  useEffect(() => {
    const fetchTTSSettings = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/chatbot/settings?code=${process.env.REACT_APP_CHATBOT_FUNCTION_KEY}`
        );
        setIsTTSGloballyEnabled(response.data.ttsEnabled !== false);
      } catch (error) {
        // Default to enabled if fetch fails
        setIsTTSGloballyEnabled(true);
      }
    };
    fetchTTSSettings();
  }, []);

  // Persist TTS mute preference
  useEffect(() => {
    localStorage.setItem('isTTSMuted', isTTSMuted);
    if (isTTSMuted) {
      // Stop AudioContext source node
      if (currentSourceRef.current) {
        try { currentSourceRef.current.stop(); } catch (e) { /* already stopped */ }
        currentSourceRef.current = null;
      }
      // Stop HTMLAudioElement (fallback)
      if (currentAudioRef.current) {
        currentAudioRef.current.pause();
        currentAudioRef.current = null;
      }
      // Cancel pending TTS request
      if (ttsAbortControllerRef.current) {
        ttsAbortControllerRef.current.abort();
        ttsAbortControllerRef.current = null;
      }
    }
  }, [isTTSMuted]);

  // Cleanup AudioContext on unmount
  useEffect(() => {
    return () => {
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  }, []);

  // Function to rephrase text using AI Phrasing API
  const rephraseText = async (text) => {
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/chatbot/aiPhrasing?code=${process.env.REACT_APP_CHATBOT_FUNCTION_KEY}`,
        { text, language }
      );

      if (response.data.success && response.data.rephrased_text) {
        return response.data.rephrased_text;
      }
      return text; // Fallback to original text if rephrasing fails
    } catch (error) {
      console.error('Error rephrasing text:', error);
      return text; // Fallback to original text
    }
  };

  // Clean URLs from text for TTS: [label](url) → label, bare URLs removed
  const cleanTextForTTS = (text) => {
    return text
      .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1') // [label](url) → label
      .replace(/https?:\/\/[^\s)]+/g, '')       // remove bare URLs
      .replace(/-/g, ' ')                       // remove dashes
      .replace(/\*/g, ' ');                       // remove stars
  };

  // Helper: Split text into sentences by period, colon, etc.
  const splitTextBySentences = (text) => {
    // Simple compatible approach: protect numbered lists, then split
    // Step 1: Replace "digit." patterns with placeholder to protect them
    const protectedText = text.replace(/(\d)\.(\s)/g, '$1___NUMPERIOD___$2');

    // Step 2: Split on sentence-ending punctuation
    const sentences = protectedText
      .split(/[.!?:]\s+/)
      .map(s => s.replace(/___NUMPERIOD___/g, '.').trim())
      .filter(s => s.length > 0);

    if (sentences.length === 0) return [text];

    // Make first chunk smaller (max 100 chars) for faster initial playback
    if (sentences[0].length > 100) {
      const firstPart = sentences[0].substring(0, 100);
      const lastSpace = firstPart.lastIndexOf(' ');
      if (lastSpace > 50) {
        const splitFirst = sentences[0].substring(0, lastSpace);
        const remainder = sentences[0].substring(lastSpace + 1);
        return [splitFirst, remainder, ...sentences.slice(1)];
      }
    }

    return sentences;
  };

  // Helper: Fetch TTS audio for a single text chunk
  const fetchTTSAudio = async (text, language, signal) => {
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/chatbot/tts?code=${process.env.REACT_APP_CHATBOT_FUNCTION_KEY}`,
      { text, language },
      { signal }
    );
    return response.data.audio;
  };

  // Helper: Play a single audio chunk
  const playAudioChunk = async (audioBase64) => {
    if (!audioBase64 || isTTSMuted) return;

    // Decode base64 to binary
    const binaryString = atob(audioBase64);
    const len = binaryString.length;
    const bytes = new Uint8Array(len);
    for (let i = 0; i < len; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }

    const audioContext = getAudioContext();

    // Primary: AudioContext (only if running — a suspended context would hang forever)
    if (audioContext) {
      if (audioContext.state === 'suspended') {
        try { await audioContext.resume(); } catch (e) { /* ignore */ }
      }

      if (audioContext.state === 'running') {
        try {
          const audioBuffer = await audioContext.decodeAudioData(bytes.buffer.slice(0));
          if (isTTSMuted) return;

          return new Promise((resolve) => {
            const source = audioContext.createBufferSource();
            source.buffer = audioBuffer;
            source.connect(audioContext.destination);
            currentSourceRef.current = source;

            source.onended = () => {
              if (currentSourceRef.current === source) {
                currentSourceRef.current = null;
              }
              resolve();
            };

            source.start(0);
          });
        } catch (decodeError) {
          // Fall through to HTMLAudioElement
        }
      }
    }

    // Fallback: HTMLAudioElement
    const audioBlob = new Blob([bytes], { type: 'audio/mpeg' });
    const audioUrl = URL.createObjectURL(audioBlob);
    const audio = new Audio(audioUrl);
    currentAudioRef.current = audio;

    return new Promise((resolve, reject) => {
      audio.onended = () => {
        URL.revokeObjectURL(audioUrl);
        if (currentAudioRef.current === audio) {
          currentAudioRef.current = null;
        }
        resolve();
      };
      audio.onerror = () => {
        URL.revokeObjectURL(audioUrl);
        if (currentAudioRef.current === audio) {
          currentAudioRef.current = null;
        }
        reject();
      };

      audio.play().catch(() => {
        URL.revokeObjectURL(audioUrl);
        currentAudioRef.current = null;
        reject();
      });
    });
  };

  // TTS function with frontend chunking for fast progressive playback
  const playTextToSpeech = async (text) => {
    if (!isTTSGloballyEnabled || isTTSMuted || !text) return;
    text = cleanTextForTTS(text);

    // If user hasn't interacted yet, store for later playback
    if (!hasUserInteractedRef.current) {
      pendingTTSTextRef.current = text;
      return;
    }

    // Stop any currently playing audio
    if (currentSourceRef.current) {
      try { currentSourceRef.current.stop(); } catch (e) { /* already stopped */ }
      currentSourceRef.current = null;
    }
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }

    // Cancel any pending TTS request
    if (ttsAbortControllerRef.current) {
      ttsAbortControllerRef.current.abort();
    }

    try {
      ttsAbortControllerRef.current = new AbortController();
      const signal = ttsAbortControllerRef.current.signal;

      // Split text into sentences for faster loading
      const sentences = splitTextBySentences(text);

      // For single sentence, use old behavior
      if (sentences.length === 1) {
        const audioBase64 = await fetchTTSAudio(text, language, signal);
        await playAudioChunk(audioBase64);
        return;
      }

      // For multiple sentences: fetch in parallel, play sequentially
      const fetchPromises = sentences.map(sentence =>
        fetchTTSAudio(sentence, language, signal).catch(() => null)
      );

      // Start playing as soon as first chunk arrives
      const audioChunks = [];
      let isPlaying = false;

      const playQueue = async () => {
        isPlaying = true;
        while (audioChunks.length > 0 && !isTTSMuted && !signal.aborted) {
          const chunk = audioChunks.shift();
          if (chunk) {
            try {
              await playAudioChunk(chunk);
            } catch (e) {
              // Continue to next chunk on error
            }
          }
        }
        isPlaying = false;
      };

      // Process chunks as they arrive
      for (let i = 0; i < fetchPromises.length; i++) {
        const audio = await fetchPromises[i];
        if (audio && !isTTSMuted && !signal.aborted) {
          audioChunks.push(audio);
          // Start playing immediately when first chunk arrives
          if (!isPlaying) {
            playQueue();
          }
        }
      }

    } catch (error) {
      // Silently handle abort/cancel errors
    }
  };

  // Function to rephrase and play TTS for form data
  const rephraseAndPlayTTS = async (text, formName) => {
    if (!isTTSGloballyEnabled || isTTSMuted || !text) return;

    try {
      let processedText = text;

      // For LeaveBalanceForm, remove sensitive keys before rephrasing
      if (formName === 'LeaveBalanceForm') {
        try {
          const data = JSON.parse(text);
          // Remove EmployeeID and TimeAccountTypeCode from each item if it's an array
          if (Array.isArray(data)) {
            const filteredData = data.map(item => {
              const { EmployeeID, TimeAccountTypeCode, ...rest } = item;
              return rest;
            });
            processedText = JSON.stringify(filteredData);
          }
        } catch (parseError) {
          console.error('Error parsing JSON in rephraseAndPlayTTS:', parseError);
        }
      }

      // First, rephrase the text to make it readable
      const rephrasedText = await rephraseText(processedText);
      // Then play the rephrased text via TTS
      await playTextToSpeech(rephrasedText);
    } catch (error) {
      console.error('Error in rephraseAndPlayTTS:', error);
    }
  };

  // Play pending TTS after user interaction
  const playPendingTTS = () => {
    if (pendingTTSTextRef.current && hasUserInteractedRef.current) {
      const text = pendingTTSTextRef.current;
      pendingTTSTextRef.current = null;
      playTextToSpeech(text);
    }
  };

  // Toggle TTS mute
  const toggleTTSMute = () => {
    ensureAudioUnlocked();
    setIsTTSMuted(prev => !prev);
  };

  // Stop current audio playback (used when user starts recording)
  const stopCurrentAudio = () => {
    // Stop AudioContext source node (primary approach)
    if (currentSourceRef.current) {
      try { currentSourceRef.current.stop(); } catch (e) { /* already stopped */ }
      currentSourceRef.current = null;
    }
    // Stop HTMLAudioElement (fallback approach)
    if (currentAudioRef.current) {
      currentAudioRef.current.pause();
      currentAudioRef.current = null;
    }
    if (ttsAbortControllerRef.current) {
      ttsAbortControllerRef.current.abort();
      ttsAbortControllerRef.current = null;
    }
  };

  useEffect(() => {
    setIsInputDisabled((!account || !showChatbot || isSessionTimedOut) && !errorMessage)
    setIsLoading((!account || !showChatbot || isSessionTimedOut) && !errorMessage)
  }, [isSessionTimedOut])

  useEffect(() => {
    if (language && messages.length === 0 && !hasFirstMessageBeenAdded.current) {
      loadFirstMessage(language);
      hasFirstMessageBeenAdded.current = true;
    } else {
      setMessages([]);
      loadFirstMessage(language);
      hasFirstMessageBeenAdded.current = true;
    }
  }, [language]);

  // Check the session status when the user back to the chat again
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // User has returned to the app, check session status
        checkSessionTimeout();
      } else {
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    localStorage.setItem('isChatOpen', isChatOpen);
  }, [isChatOpen]);

  useEffect(() => {
    if (language) {
      localStorage.setItem('language', language);
    }
  }, [language]);

  useEffect(() => {
    sessionStorage.setItem('sessionID', sessionId);
    startSessionTimeout();
  }, [sessionId]);




  // Add session timeout
  const startSessionTimeout = () => {
    const timeoutDuration = 55 * 60 * 1000; // 55 minutes
    const expirationTime = new Date().getTime() + timeoutDuration;
    localStorage.setItem('session_expiration', expirationTime);
    localStorage.setItem('session_renewed', true);
    setTimeout(() => {
      checkSessionTimeout();
    }, timeoutDuration);
  };

  // Check session timeout
  const checkSessionTimeout = () => {
    const expirationTime = localStorage.getItem('session_expiration');
    if (expirationTime && new Date().getTime() > expirationTime) {
      localStorage.removeItem('id_token');
      localStorage.removeItem('session_expiration');
      localStorage.setItem('session_renewed', false);
      setLoginProgress("Start")
      setShowChatbot(false);
      setIsSessionTimedOut(true);
      setAccount(null);
      setShowAuthFallback(true);
    }
  };

  useEffect(() => {
    if (isSessionTimedOut || errorMessage) {
      setShowAuthFallback(true);
      setIsLoading(false)
    } else {
      setShowAuthFallback(false);
    }
  }, [isSessionTimedOut, errorMessage]);

  const sanitizeText = (text, isBot = false) => {
    if (isBot) {
      // For bot messages, allow safe HTML tags like links
      return DOMPurify.sanitize(text, {
        ALLOWED_TAGS: ['a', 'br', 'p', 'strong', 'em'],
        ALLOWED_ATTR: ['href', 'class', 'target', 'rel']
      });
    }
    // For user messages, strip all HTML
    return DOMPurify.sanitize(text, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  };

  // Add new message
  const addMessage = (message) => {
    const timestamp = new Date().getTime();

    // sanitize text content safely
    const cleanMessage = {
      ...message,
      text: message?.isForm ? message.text : sanitizeText(message.text || "", message.sender === 'bot'),
      timestamp
    };

    setMessages((prevMessages) => {
      const updatedMessages = [...prevMessages, cleanMessage];
      sessionStorage.setItem('history', JSON.stringify(updatedMessages));
      return updatedMessages;
    });

    // Mark user as interacted when they send a message
    if (message.sender === 'user') {
      if (!hasUserInteractedRef.current) {
        hasUserInteractedRef.current = true;
        ensureAudioUnlocked();
        // Play any pending TTS (like welcome message) after first interaction
        setTimeout(() => playPendingTTS(), 100);
      }
    }

    // Trigger TTS for bot messages that are not forms (or specific form types) and have text
    const ttsEnabledForms = ['GetHRLawRelevantInfo', 'GetRelevantInfo'];
    const ttsFormsThatNeedRephrasing = ['LeaveBalanceForm'];

    // Skip TTS for welcome message - it will play after user interaction
    const isWelcomeMessage = message.type === 'welcome';

    const shouldPlayTTS = message.sender === 'bot' &&
      message.text &&
      !isWelcomeMessage &&
      (!message.isForm || ttsEnabledForms.includes(message.formName));

    const shouldPlayTTSWithRephrasing = message.sender === 'bot' &&
      message.text &&
      message.isForm &&
      ttsFormsThatNeedRephrasing.includes(message.formName);

    if (shouldPlayTTS) {
      const textToSpeak = sanitizeText(message.text);
      if (textToSpeak) {
        playTextToSpeech(textToSpeak);
      }
    } else if (shouldPlayTTSWithRephrasing) {
      // For forms like LeaveBalanceForm, rephrase the data before playing TTS
      if (message.text) {
        // Check if the data is empty (empty array or no meaningful data)
        const isEmptyData = Array.isArray(message.text) && message.text.length === 0;

        if (isEmptyData) {
          // If no leave balance, play the empty message
          playTextToSpeech(t("Sorry, you don't have leave balance"));
        } else {
          // Otherwise, rephrase and play the data
          rephraseAndPlayTTS(JSON.stringify(message.text), message.formName);
        }
      }
    }
  };

  // Load welcome message
  const loadFirstMessage = (lang) => {
    let message = t("Hi, I'm TAMM, your digital assistant. I can help you with Human Resources services.");

    const isManager = localStorage.getItem("IsManager"); // e.g., "manager" or "employee"
    const buttons = [
      { label: t('Leaves'), value: 'leaves', isNew: false },
        { label: t('Permissions'), value: 'permissions', isNew: false },
        { label: t('Remote Work'), value: 'remote_work', isNew: false },
        { label: t('HR Certificates'), value: 'hr_certificates', isNew: false },
        { label: t('Raise a Ticket'), value: 'raise_support_ticket', isNew: false },
    ];
    // Only add "Show More" if user is manager
    if (isManager == "true") {
      buttons.push({ label: t('Show More'), value: 'show_more', isNew: false });
    }

    addMessage({
      sender: 'bot',
      text: message,
      type: "welcome",
      buttons: buttons
    });
  };

  // Select language
  const selectLanguage = (lang) => {
    if (i18n.language !== lang) {
      setLanguage(lang);
      i18n.changeLanguage(lang);
    }
    setDirection(lang === 'ar' ? 'rtl' : 'ltr');
  };

  // Trim history after clearHistory found in the messages
  const trimHistoryAfterClear = (messages) => {
    const lastClearIndex = messages.findIndex((msg) => msg.clearHistory || (msg && msg.formName === 'UpdateLeave'));
    return lastClearIndex !== -1 ? messages.slice(lastClearIndex + 1) : messages;
  };

  // Handle send message
  const handleSend = async (input, status = null) => {
    if (input.trim()) {
      const text = DOMPurify.sanitize(input); // Sanitize the input to remove any malicious code

      if (status !== "hidden") {
        addMessage({ sender: 'user', text: text });
      }

      setPlaceholder(t('Typing...'));
      setIsTyping(true);
      let frontendLog = JSON.stringify({ "sessionID": sessionStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language'), isAudioOn: localStorage.getItem("isTTSMuted") !== 'true' })

      const trimmedMessages = trimHistoryAfterClear(messages);
      if (trimmedMessages.some(msg => msg.formName === "EmployeesLeavesBalances")) {
        trimmedMessages.length = 0;
      }

      try {
        const idToken = localStorage.getItem('id_token');
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/chatbot/chat?code=${process.env.REACT_APP_CHATBOT_FUNCTION_KEY}`, {
          language: language,
          message: DOMPurify.sanitize(text),
          sessionId: sessionId,
          messages: sessionStorage.getItem('clearHistory') === 'true' ? [] : trimmedMessages.slice(1),
          token: idToken,
          employeeID: localStorage.getItem('EmployeeID'),
          frontendLog: frontendLog
        });
        addMessage({
          sender: 'bot',
          text: response.data.message,
          isForm: response.data.isFormData,
          isLeaveRequest: response.data.isLeaveRequest,
          leaveRequestData: response.data.message,
          messageID: response.data.messageID,
          // title: response.data.title,
          custom: response?.data?.custom,
          formName: response.data.formName
        });
      } catch (error) {
        addMessage({
          sender: 'bot',
          text: t('There was an error occurred, Please try again.')
        });
      } finally {
        setPlaceholder(t('Type your message or use voice recording...'));
        setIsTyping(false);
      }
    }
  };

  const toggleChat = () => {
    // Mark user interaction for TTS (clicking chat icon counts as interaction)
    hasUserInteractedRef.current = true;
    ensureAudioUnlocked();
    playPendingTTS();

    if (isMobile) {
      window.open(process.env.REACT_APP_PUBLIC_URL, '_system');
    } else {
      setIsChatOpen((prevIsChatOpen) => {
        const newIsChatOpen = !prevIsChatOpen;

        if (newIsChatOpen) {
          setShowChatbot(false)
          handleLogin();
        }

        localStorage.setItem("isChatOpen", newIsChatOpen);

        return newIsChatOpen;
      });
    }
  };

  const hasCodeParam = () => {
    const params = new URLSearchParams(window.location.search);
    return Boolean(params.get('code'));
  };

  useEffect(() => {
    try {
      if (isChatOpen && !isMobile && !hasCodeParam()) {
        handleLogin();
      }
    } catch (e) {

    }
  }, [isChatOpen]);

  useEffect(() => {
    if (!hasCodeParam() && !account && !showChatbot) {
      handleLogin();
    }
  }, [isMobile]);

  function getCurrentCode() {
    const params = new URLSearchParams(window.location.search);
    return params.get('code');
  }
  const initializeMsal = async () => {
    await msalInstance.initialize();
    const accounts = msalInstance.getAllAccounts();
    const expirationTime = localStorage.getItem('session_expiration');
    if (expirationTime && !(new Date().getTime() > expirationTime)) {
      if (accounts.length > 0) {
        setAccount(accounts[0]);
      }
    } else {
      setAccount(null);
    }
  };

  useEffect(() => {
    async function runAuthFlow() {
      const code = getCurrentCode();

      if (!code) {
        handleLogin();
        return;
      }

      const usedCodes = JSON.parse(sessionStorage.getItem('usedCodes') || '[]');
      if (usedCodes.includes(code)) {
        // Remove code from URL to avoid infinite loop
        const url = new URL(window.location.href);
        url.searchParams.delete('code');
        window.history.replaceState({}, document.title, url.pathname + url.search);

        handleLogin();
        return;
      }

      try {
        setLoginProgress('Loading...');
        await fetchAndStoreIASToken(code);
        await fetchData();
        setShowChatbot(true);
        startSessionTimeout();
        setAccount(true);
        setIsSessionTimedOut(false);

        sessionStorage.setItem(
          'usedCodes',
          JSON.stringify([...usedCodes, code])
        );
      } catch (e) {
        setShowChatbot(false);
        setAccount(false);
        setIsLoading(false)
        setIsSessionTimedOut(true);
      }
    }

    runAuthFlow();
  }, [window.location.search]); // If you use react-router, you might want to use 'location' instead  

  async function fetchAndStoreIASToken(code) {
    const params = new URLSearchParams({
      grant_type: process.env.REACT_APP_QPASS_AUTH_GRANT_TYPE,
      code: code,
      redirect_uri: process.env.REACT_APP_QPASS_REDIRECT_URI,
      client_id: process.env.REACT_APP_QPASS_CLIENT_ID,
      client_secret: process.env.REACT_APP_QPASS_CLIENT_SECRET
    });

    // 2) POST to the token endpoint
    const res = await fetch(
      process.env.REACT_APP_IAS_AUTH_URL,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString()
      }
    );

    if (!res.ok) {
      const errText = await res.text();
      // throw new Error(`Token request failed (${res.status}): ${errText}`);
    }

    // 3) Parse JSON and pull out the access token
    const { id_token } = await res.json();

    if (!id_token) {
      // throw new Error('No id_token in response');
    }
    // console.log(id_token)
    // 4) Save it in localStorage under "id_token"
    localStorage.setItem('id_token', id_token);
    sessionStorage.setItem('isCodeUsed', true)
  }
  const handleQatarPassLogin = async () => {
    try {
      setErrorMessage("")
      setLoginProgress("Loading...")
      // Remove potential code parameter from URL to prevent infinite code grant loop
      const url = new URL(window.location.href);
      url.searchParams.delete('code');
      window.history.replaceState({}, document.title, url.pathname + url.search);
      login();
      setShowChatbot(true)
      startSessionTimeout();
      setIsSessionTimedOut(false);
      setLoginProgress("Start")
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      setErrorMessage(t('Loading...'))
      console.error(error);
    }
  }
  const handleEmailLogin = async () => {
    try {
      setIsAuthenticating(true)
      setErrorMessage("")
      // Must run synchronously before first await to stay inside the click event gesture
      hasUserInteractedRef.current = true;
      ensureAudioUnlocked();
      await initializeMsal();
      const loginResponse = await msalInstance.loginPopup(loginRequest);
      localStorage.setItem('id_token', loginResponse.accessToken);
      setAccount(loginResponse.account);
      await fetchData();
      setShowChatbot(true)
      startSessionTimeout();
      setIsSessionTimedOut(false);
      setErrorMessage("")
    } catch (error) {
      console.error(error);
      setShowChatbot(true)
      startSessionTimeout();
      setIsSessionTimedOut(false);
      setErrorMessage(t('Loading...'))
    }
  }

  // useEffect(() => {
  //   handleLogin()
  // }, [])

  const handleLogin = async () => {
    try {
      setErrorMessage("");
      console.log(authMethod)
      if (authMethod === "qatarpass") {
        // Qatar Pass Login
        handleQatarPassLogin()
      } 
      else if (authMethod === "email") {
        // MSAL Login
        handleEmailLogin();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchData = async () => {
    let frontendLog = JSON.stringify({ "sessionID": sessionStorage.getItem('sessionID'), "EmployeeID": localStorage.getItem('EmployeeID'), "channel": isMobile ? "Mobile" : "Portal", "language": localStorage.getItem('language') })
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/middleware/getCurrentEmployee?code=${process.env.REACT_APP_MIDDLEWARE_FUNCTION_KEY}&token=${encodeURIComponent(localStorage.getItem('id_token'))}&sessionID=${encodeURIComponent(sessionStorage.getItem('sessionID'))}&frontendLog=${encodeURIComponent(frontendLog)}`);
    if (!response.ok) {
      setErrorMessage(t('Unauthorized user'))
      sessionStorage.setItem('isCodeUsed', true)
      setIsLoading(false)
      setIsAuthenticating(false)
      throw new Error('Network response was not ok');
    }
    setIsAuthenticating(false)
    const result = await response.json();
    localStorage.setItem("EmployeeID", result.EmployeeID);
    localStorage.setItem("IsManager", result.IsManager);
    setIsLoading(false)
    setShowChatbot(true)
    setErrorMessage("")
    // Queue greeting and play immediately if user has already interacted (e.g. EntraID popup flow)
    const greetingText = t("Hi, I'm TAMM, your digital assistant. I can help you with Human Resources services.");
    pendingTTSTextRef.current = greetingText;
    playPendingTTS();
  };

  useEffect(() => {
    const setPlaceholderText = () => {
      const placeholderText = t('Type your message or use voice recording...');
      setPlaceholder(placeholderText);
    };

    setPlaceholderText();
  }, [language, t]);

  return (
    <>
      <div ref={nodeRef} className={`app-container chatbot-container no-select ${direction}  ${!showChatUI || selectedOption == '' ? 'home-page' : ''} ${isChatOpen ? 'open' : ''}`}>
        {!isMobile && <div className='chat-bot-icon-container'>
          <FontAwesomeIcon icon={faArrowsAlt} className='chatbot-moveable' />
          <img
            className='chat-icon'
            src={`${process.env.REACT_APP_PUBLIC_URL}/bot-avatar-2.png`}
            height="55px"
            width="55px"
            onClick={toggleChat}
            onTouchStart={toggleChat}
            ref={chatIconRef}
          />
        </div>}

        {(isChatOpen || isMobile) && (
          <div className="chat-container">
            <ChatHeader onCloseChat={toggleChat} setMessages={setMessages} selectLanguage={selectLanguage} isTyping={isTyping} hasFirstMessageBeenAdded={hasFirstMessageBeenAdded} loadFirstMessage={loadFirstMessage} isLoading={isLoading} setSelectedOption={setSelectedOption} />
            {loading ? (
              <div>
                <p className='loading'>{t('Loading...')}</p>
              </div>
            ) : !language ? (
              <LanguageSelection onSelectLanguage={selectLanguage} />
            ) :
              (
                <>
                  { 
                    false ? (
                      <div className="centered-h-v-column">
                        <button onClick={handleLogin} className='login-btn'>
                          <span>{t("Login")}</span>
                        </button>
                        {errorMessage && <span className={errorMessage == t('Loading...') ? '' : 'error-message'}>{t(errorMessage)}</span>}
                        {isSessionTimedOut && !errorMessage && <span className='error-message'>{t("Session timed out. Please login again")}</span>}
                      </div>
                    ) : (
                      !showChatUI || selectedOption == '' ? (
                        <LandingPage
                          onSelectOption={(option) => {
                            hasUserInteractedRef.current = true;
                            ensureAudioUnlocked();
                            playPendingTTS();
                            setSelectedOption(option);
                            setShowChatUI(true);
                          }} />
                      ) : (
                        <>
                        {selectedOption == "services" ? 
                          <ChatBody
                            messages={messages}
                            language={language}
                            onSendMessage={addMessage}
                            addFirstMessage={loadFirstMessage}
                            setMessages={setMessages}
                            disabled={isInputDisabled}
                            handleSend={handleSend}
                            selectedOption={selectedOption}
                          /> : <FAQs setSelectedOption={setSelectedOption} />}
                          <MessageInput
                            ref={messageInputRef}
                            onSendMessage={addMessage}
                            disabled={isInputDisabled}
                            language={language}
                            sessionId={sessionId}
                            placeholderText={placeholder}
                            messages={messages}
                            isTyping={isTyping}
                            setSelectedOption={setSelectedOption}
                            isTTSMuted={isTTSMuted}
                            toggleTTSMute={toggleTTSMute}
                            isTTSGloballyEnabled={isTTSGloballyEnabled}
                            stopCurrentAudio={stopCurrentAudio}
                          />
                        </>
                      )
                    )
                  }
                </>
              )}
          </div>
        )}
      </div>
    </>

  );
};

export default App;